import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider as MuiDivider,
  FormControl,
  GlobalStyles,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import { toast } from 'react-toastify';
import isEmail from 'validator/lib/isEmail';

import { SWLoadingButton } from '@/components/LoadingButton';
import { OrderIcon } from '@/components/OrderIcon';
import { PhoneNumberInput } from '@/components/PhoneNumberInput';
import { naturalCompare } from '@/util';
import { formatPhoneNumberForDisplay } from '@/utils/phoneUtils';

import {
  IAccountWithUserAccounts,
  IAddUserAccountRequest,
  IRole,
  IUserAccountBase,
} from '../types';
import { deleteUser, listRoles, setMemberRole } from '../api';

type SortField = 'email' | 'phone' | 'description' | 'role' | 'status' | 'notifications';
type SortOrder = 'asc' | 'desc';

const STATUS_LABELS: Record<string, string> = {
  UNKNOWN: 'Unknown',
  UNCONFIRMED: 'Invite Sent',
  CONFIRMED: 'Active',
  FORCE_CHANGE_PASSWORD: 'Reset Password',
};

const ROLE_DESCRIPTIONS: Record<string, string> = {
  owner: 'Full account control - all permissions. Cannot be changed by admins',
  admin: 'Full account control - same permissions as owner. Cannot modify owner roles',
  billing_manager:
    'View/manage billing and tenants only. Cannot invite users or view usage/tenant alerts',
};

export function TeamView({
  account,
  currentUserId,
  onInvite,
  onResend,
  onUpdate,
  onRoleChange,
  onDelete,
}: {
  account: IAccountWithUserAccounts;
  currentUserId: string;
  onInvite: (body: IAddUserAccountRequest) => Promise<void>;
  onResend: (userId: string, inviteMemo: string | null) => Promise<void>;
  onUpdate: (
    user: IUserAccountBase,
    updates: { note?: string | null; phone_number?: string | null },
  ) => Promise<void>;
  onRoleChange?: (userId: string, newRoleKey: string) => Promise<void>;
  onDelete?: (userId: string) => Promise<void>;
}) {
  const [orderBy, setOrderBy] = React.useState<SortField>('email');
  const [order, setOrder] = React.useState<SortOrder>('asc');

  // modal state
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [description, setDescription] = React.useState('');

  // edit user dialog state
  const [editUserDialogOpen, setEditUserDialogOpen] = React.useState(false);
  const [editingUser, setEditingUser] = React.useState<IUserAccountBase | null>(null);
  const [editUserPhone, setEditUserPhone] = React.useState('');
  const [editUserNote, setEditUserNote] = React.useState('');

  // delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [userToDelete, setUserToDelete] = React.useState<IUserAccountBase | null>(null);
  const [deletingUser, setDeletingUser] = React.useState(false);

  // menu state
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuUserId, setMenuUserId] = React.useState<string | null>(null);
  const [inviteMemo, setInviteMemo] = React.useState('');
  const [inviteRoleKey, setInviteRoleKey] = React.useState('owner');
  const [emailError, setEmailError] = React.useState(false);
  const [phoneError, setPhoneError] = React.useState(false);
  const [inviteLoading, setInviteLoading] = React.useState(false);
  // resending spinner per row
  const [resendingId, setResendingId] = React.useState<string | null>(null);
  // updating user in dialog
  const [updatingUser, setUpdatingUser] = React.useState(false);

  // RBAC state
  const [roles, setRoles] = React.useState<IRole[]>([]);
  const [changingRoleUserId, setChangingRoleUserId] = React.useState<string | null>(null);
  const [selectResetKey, setSelectResetKey] = React.useState(0);

  // self-downgrade confirmation dialog state
  const [downgradeDialogOpen, setDowngradeDialogOpen] = React.useState(false);
  const [pendingDowngrade, setPendingDowngrade] = React.useState<{
    userId: string;
    newRoleKey: string;
  } | null>(null);

  // Fetch available roles on mount
  React.useEffect(() => {
    listRoles()
      .then((response) => {
        if (response?.roles) {
          setRoles(response.roles);
        }
      })
      .catch((err) => {
        console.error('Failed to load roles:', err);
      });
  }, []);

  const STATUS_CHIP_COLOR: Record<
    string,
    'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'
  > = {
    UNKNOWN: 'default',
    UNCONFIRMED: 'warning',
    CONFIRMED: 'success',
    FORCE_CHANGE_PASSWORD: 'info',
  };

  const handleSort = (field: SortField) => {
    if (orderBy === field) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(field);
      setOrder('asc');
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, userId: string) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuUserId(userId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuUserId(null);
  };

  const handleEditClick = () => {
    const user = account.associated_user_accounts.find((u) => u.id === menuUserId);
    if (user) {
      setEditingUser(user);
      setEditUserPhone(user.phonenumber || '');
      setEditUserNote(user.note || '');
      setEditUserDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleEditSave = async () => {
    if (!editingUser) return;

    setUpdatingUser(true);
    try {
      await onUpdate(editingUser, {
        phone_number: editUserPhone || null,
        note: editUserNote.trim() || null,
      });
      toast.success('User updated successfully!', { closeButton: true });
      setEditUserDialogOpen(false);
      setEditingUser(null);
      setEditUserPhone('');
      setEditUserNote('');
    } catch (err: any) {
      // Error messages from api.ts are already user-friendly
      toast.error(err.message || 'Failed to update user', { closeButton: true });
    } finally {
      setUpdatingUser(false);
    }
  };

  const handleEditCancel = () => {
    setEditUserDialogOpen(false);
    setEditingUser(null);
    setEditUserPhone('');
    setEditUserNote('');
  };

  const handleDeleteClick = () => {
    const user = account.associated_user_accounts.find((u) => u.id === menuUserId);
    if (user) {
      setUserToDelete(user);
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setDeletingUser(true);
    try {
      if (onDelete) {
        await onDelete(userToDelete.id);
      } else {
        await deleteUser(userToDelete.id);
      }
      toast.success('Team member removed successfully!', { closeButton: true });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (err: any) {
      toast.error(`Failed to remove team member: ${err.message || err}`, { closeButton: true });
    } finally {
      setDeletingUser(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const executeRoleChange = async (userId: string, newRoleKey: string) => {
    setChangingRoleUserId(userId);
    try {
      if (onRoleChange) {
        await onRoleChange(userId, newRoleKey);
      } else {
        await setMemberRole(userId, newRoleKey);
      }
      toast.success('Role updated successfully!', { closeButton: true });
    } catch (err: any) {
      // Parse permission errors for more helpful messages
      const errorMessage = err.message || String(err);

      if (errorMessage.includes('Cannot demote the last owner')) {
        toast.error('Cannot demote the last owner. Assign another owner first.', {
          closeButton: true,
          autoClose: 5000,
        });
      } else if (errorMessage.includes('MANAGE_TEAM_MEMBERS')) {
        toast.error(
          "You don't have permission to change team member roles. Please contact your account owner.",
          { closeButton: true, autoClose: 5000 },
        );
      } else if (errorMessage.includes('Missing required permissions')) {
        toast.error(
          "You don't have permission to perform this action. Please contact your account owner.",
          { closeButton: true, autoClose: 5000 },
        );
      } else if (errorMessage.includes('Only owners can modify other owners')) {
        toast.error('Only owners can modify other owners.', { closeButton: true, autoClose: 5000 });
      } else if (errorMessage.includes('Only owners can grant the owner role')) {
        toast.error('Only owners can grant the owner role.', {
          closeButton: true,
          autoClose: 5000,
        });
      } else if (errorMessage.includes('Only owners')) {
        toast.error('Only owners can perform this action.', { closeButton: true, autoClose: 5000 });
      } else {
        toast.error(`Failed to update role: ${errorMessage}`, { closeButton: true });
      }

      // Force Select to remount with correct value by changing key
      setSelectResetKey((prev) => prev + 1);
    } finally {
      setChangingRoleUserId(null);
    }
  };

  const handleRoleChange = async (userId: string, newRoleKey: string) => {
    const isCurrentUser = userId === currentUserId;
    const currentUser = account.associated_user_accounts.find((u) => u.id === userId);
    const isDowngradeFromOwner = currentUser?.role_key === 'owner' && newRoleKey !== 'owner';

    if (isCurrentUser && isDowngradeFromOwner) {
      const ownerCount = account.associated_user_accounts.filter(
        (u) => u.role_key === 'owner',
      ).length;

      if (ownerCount <= 1) {
        toast.error(
          'You are the only owner on this account. You must assign another owner before downgrading your role.',
          { closeButton: true, autoClose: 5000 },
        );
        setSelectResetKey((prev) => prev + 1);
        return;
      }

      // Show confirmation dialog
      setPendingDowngrade({ userId, newRoleKey });
      setDowngradeDialogOpen(true);
      return;
    }

    await executeRoleChange(userId, newRoleKey);
  };

  const handleDowngradeConfirm = async () => {
    if (!pendingDowngrade) return;
    setDowngradeDialogOpen(false);
    await executeRoleChange(pendingDowngrade.userId, pendingDowngrade.newRoleKey);
    setPendingDowngrade(null);
  };

  const handleDowngradeCancel = () => {
    setDowngradeDialogOpen(false);
    setPendingDowngrade(null);
    setSelectResetKey((prev) => prev + 1);
  };

  const sortedRows = React.useMemo(() => {
    return [...account.associated_user_accounts].sort((a, b) => {
      let aKey: string | number;
      let bKey: string | number;
      switch (orderBy) {
        case 'email':
          aKey = a.email;
          bKey = b.email;
          break;
        case 'phone':
          aKey = a.phonenumber || '';
          bKey = b.phonenumber || '';
          break;
        case 'description':
          aKey = a.note || '';
          bKey = b.note || '';
          break;
        case 'role':
          aKey = a.role_key || 'owner';
          bKey = b.role_key || 'owner';
          break;
        case 'status':
          aKey = STATUS_LABELS[a.user_status] ?? a.user_status;
          bKey = STATUS_LABELS[b.user_status] ?? b.user_status;
          break;
        case 'notifications':
          aKey =
            (a.enabled_email_notifications?.length || 0) +
            (a.enabled_sms_notifications?.length || 0);
          bKey =
            (b.enabled_email_notifications?.length || 0) +
            (b.enabled_sms_notifications?.length || 0);
          break;
        default:
          aKey = '';
          bKey = '';
      }
      const cmp =
        typeof aKey === 'number' ? aKey - (bKey as number) : naturalCompare(aKey, bKey as string);
      return order === 'asc' ? cmp : -cmp;
    });
  }, [account, orderBy, order]);

  const handleInvite = async () => {
    // empty email is invalid
    if (!email.trim()) {
      setEmailError(true);
      return;
    }
    // use validator.js for email validation
    if (!isEmail(email.trim())) {
      setEmailError(true);
      return;
    }
    // check note length
    if (description.trim().length > 32) {
      setEmailError(true);
      return;
    }
    setEmailError(false);
    setInviteLoading(true);
    try {
      await onInvite({
        email: email.trim(),
        note: description.trim(), // persist as "note" on backend
        invite_memo: inviteMemo.trim(), // one-off invite email text
        phone_number: phoneNumber || undefined,
        role_key: inviteRoleKey,
      });
      toast.success('Invite sent!', { closeButton: true });
      closeDialog();
    } catch (err: any) {
      // Error messages are already user-friendly from api.ts
      toast.error(err.message || 'Failed to send invitation', { closeButton: true });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleResend = async (userId: string, memo: string | null) => {
    setResendingId(userId);
    try {
      await onResend(userId, memo);
      toast.success('Invite resent!', { closeButton: true });
    } catch (err: any) {
      // Error messages are already user-friendly from api.ts
      toast.error(err.message || 'Failed to resend invite', { closeButton: true });
    } finally {
      setResendingId(null);
    }
  };

  const openDialog = () => {
    setEmail('');
    setPhoneNumber('');
    setDescription('');
    setInviteMemo('');
    setInviteRoleKey('owner');
    setEmailError(false);
    setPhoneError(false);
    setInviteLoading(false);
    setInviteOpen(true);
  };
  const closeDialog = () => {
    setInviteOpen(false);
    setEmail('');
    setPhoneNumber('');
    setDescription('');
    setInviteMemo('');
    setInviteRoleKey('owner');
    setEmailError(false);
    setPhoneError(false);
    setInviteLoading(false);
  };

  return (
    <>
      <GlobalStyles
        styles={{
          // pill shape on all outlined inputs
          '.MuiOutlinedInput-root': {
            borderRadius: '14px !important',
            minHeight: '45px !important',
          },
          '.MuiOutlinedInput-input': {
            padding: '8px 12px !important',
          },
          // dark-blue focus ring
          '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 2px rgba(71,144,204,0.2) !important',
          },
          // also select outlines
          '.MuiSelect-outlined .MuiOutlinedInput-notchedOutline': {
            borderRadius: '14px !important',
          },
          // round the dialog itself
          '.MuiDialog-paper': {
            borderRadius: '14px',
          },
        }}
      />
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ mt: 2, borderRadius: 2, boxShadow: 'none' }}
      >
        <Box margin={3}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight={600}>
              Team Members
            </Typography>
            <SWLoadingButton variant="contained" onClick={openDialog}>
              Invite New User
            </SWLoadingButton>
          </Stack>
        </Box>
        <MuiDivider sx={{ borderColor: 'divider' }} />

        <Table size="medium">
          <TableHead>
            <TableRow sx={{ '& th': { borderBottom: 1, borderColor: 'divider' } }}>
              {[
                { field: 'email', label: 'Email' },
                { field: 'phone', label: 'Phone' },
                { field: 'description', label: 'Description' },
                { field: 'role', label: 'Role' },
                { field: 'status', label: 'Status' },
                { field: 'notifications', label: 'Notifications' },
                { field: 'actions', label: 'Actions' },
              ].map(({ field, label }) => (
                <TableCell
                  key={field}
                  align="center"
                  sortDirection={orderBy === field ? order : false}
                >
                  {field === 'actions' ? (
                    label
                  ) : field === 'role' ? (
                    <Box display="inline-flex" alignItems="center">
                      <TableSortLabel
                        active={orderBy === field}
                        direction={orderBy === field ? order : 'asc'}
                        onClick={() => handleSort(field as SortField)}
                        IconComponent={() => <OrderIcon type={orderBy === field ? order : ''} />}
                      >
                        {label}
                      </TableSortLabel>
                      <Tooltip
                        title={
                          <Box sx={{ maxWidth: 320 }}>
                            <Typography variant="body2" fontWeight={600} mb={1}>
                              Role Permissions
                            </Typography>
                            {roles
                              .slice()
                              .sort((a, b) => {
                                const order = { owner: 0, admin: 1, billing_manager: 2 };
                                return (order[a.role_key] ?? 999) - (order[b.role_key] ?? 999);
                              })
                              .map((role) => (
                                <Typography key={role.role_key} variant="body2" mb={0.5}>
                                  <strong>{role.role_name}:</strong>{' '}
                                  {ROLE_DESCRIPTIONS[role.role_key] || role.description}
                                </Typography>
                              ))}
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <IconButton size="small" sx={{ p: 0.5 }}>
                          <InfoOutlinedIcon sx={{ fontSize: '17px', color: '#555' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <TableSortLabel
                      active={orderBy === field}
                      direction={orderBy === field ? order : 'asc'}
                      onClick={() => handleSort(field as SortField)}
                      IconComponent={() => <OrderIcon type={orderBy === field ? order : ''} />}
                    >
                      {label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedRows.map((u) => {
              const isCurrentUser = u.id === currentUserId;
              return (
                <TableRow
                  key={u.id}
                  sx={{
                    backgroundColor: isCurrentUser ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                    '&:hover': {
                      backgroundColor: isCurrentUser ? 'rgba(25, 118, 210, 0.12)' : 'action.hover',
                    },
                  }}
                >
                  <TableCell
                    align="center"
                    sx={{
                      maxWidth: 250,
                      minWidth: 200,
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {isCurrentUser && (
                      <Chip
                        label="You"
                        size="small"
                        sx={{
                          mr: 1,
                          height: 20,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                        color="primary"
                      />
                    )}
                    {u.email}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 200,
                      position: 'relative',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {formatPhoneNumberForDisplay(u.phonenumber)}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      width: 300, // step 1: lock in a width
                      position: 'relative', // step 2: positioning context
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {u.note || '—'}
                  </TableCell>
                  <TableCell align="center" sx={{ width: 150 }}>
                    {roles.length > 0 ? (
                      <Tooltip
                        title={
                          (u.role_key && ROLE_DESCRIPTIONS[u.role_key]) ||
                          roles.find((r) => r.role_key === u.role_key)?.description ||
                          ''
                        }
                        arrow
                        placement="left"
                      >
                        <FormControl
                          size="small"
                          fullWidth
                          key={`${u.id}-${u.role_key}-${selectResetKey}`}
                        >
                          <Select
                            value={u.role_key || 'owner'}
                            onChange={(e) => handleRoleChange(u.id, e.target.value)}
                            disabled={changingRoleUserId === u.id}
                            sx={{
                              borderRadius: '14px',
                              fontSize: '0.875rem',
                            }}
                          >
                            {roles
                              .slice()
                              .sort((a, b) => {
                                const order = { owner: 0, admin: 1, billing_manager: 2 };
                                return (order[a.role_key] ?? 999) - (order[b.role_key] ?? 999);
                              })
                              .map((role) => (
                                <MenuItem key={role.role_key} value={role.role_key}>
                                  {role.role_name}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {u.role_key || '—'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={STATUS_LABELS[u.user_status] ?? u.user_status}
                      color={STATUS_CHIP_COLOR[u.user_status] ?? 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ alignContent: 'vertical' }}>
                    <Stack spacing={0.5} alignItems="center">
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="caption" sx={{ minWidth: 35, textAlign: 'right' }}>
                          Email:
                        </Typography>
                        {u.enabled_email_notifications &&
                        u.enabled_email_notifications.length > 0 ? (
                          <Chip
                            label={`${u.enabled_email_notifications.length}`}
                            color="success"
                            size="small"
                            sx={{ height: 20, fontSize: '0.6rem' }}
                          />
                        ) : (
                          <Chip
                            label="Off"
                            color="default"
                            size="small"
                            sx={{ height: 20, fontSize: '0.6rem' }}
                          />
                        )}
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="caption" sx={{ minWidth: 35, textAlign: 'right' }}>
                          SMS:
                        </Typography>
                        {u.enabled_sms_notifications && u.enabled_sms_notifications.length > 0 ? (
                          <Chip
                            label={`${u.enabled_sms_notifications.length}`}
                            color="success"
                            size="small"
                            sx={{ height: 20, fontSize: '0.6rem' }}
                          />
                        ) : (
                          <Chip
                            label="Off"
                            color="default"
                            size="small"
                            sx={{ height: 20, fontSize: '0.6rem' }}
                          />
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  {u.user_status === 'CONFIRMED' ? (
                    <TableCell align="center">
                      <IconButton size="small" onClick={(event) => handleMenuClick(event, u.id)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  ) : (
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <SWLoadingButton
                          size="small"
                          onClick={() => handleResend(u.id, null)}
                          loading={resendingId === u.id}
                        >
                          Resend Invite
                        </SWLoadingButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setUserToDelete(u);
                            setDeleteDialogOpen(true);
                          }}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {/* if no team members, show a message. technically impossible but we cover edge cases anyways cuz we're cool like that B-) */}
            {account.associated_user_accounts.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
                  No team members.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={inviteOpen}
        onClose={closeDialog}
        aria-labelledby="invite-user-dialog-title"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <GlobalStyles
          styles={{
            '.MuiOutlinedInput-root': {
              borderRadius: '14px !important',
              minHeight: '45px !important',
              padding: '0 !important',
            },
            '.MuiOutlinedInput-input': {
              padding: '8px 12px !important',
              lineHeight: '1.5em',
            },
            '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              boxShadow: '0 0 0 2px rgba(71,144,204,0.2) !important',
            },
          }}
        />
        <DialogTitle
          id="invite-user-dialog-title"
          sx={{ margin: 'auto', textAlign: 'center', paddingTop: 3 }}
        >
          <Stack direction={'column'}>
            <Box sx={{ fontSize: '32px', fontWeight: 'bold' }}>Invite New User</Box>
            <Box sx={{ fontSize: '16px', color: 'text.secondary' }}>
              Add a new teammate to your account
            </Box>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={closeDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <MuiDivider
          variant="fullWidth"
          sx={{ borderWidth: 1, borderColor: 'grey', marginTop: 2 }}
        />

        <DialogContent sx={{ margin: 3 }}>
          <Stack spacing={3}>
            <Box>
              <Typography fontSize="16px" fontWeight={400} mb={1}>
                Email Address *
              </Typography>
              <TextField
                type="email"
                placeholder="e.g. alice@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(false);
                }}
                variant="outlined"
                fullWidth
                error={emailError}
                helperText={
                  emailError
                    ? email.trim()
                      ? 'Please enter a valid email address'
                      : 'Email is required'
                    : undefined
                }
              />
            </Box>

            <Box>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography fontSize="16px" fontWeight={400}>
                  Role *
                </Typography>
                <Tooltip
                  title={
                    <Box sx={{ maxWidth: 320 }}>
                      <Typography variant="body2" fontWeight={600} mb={1}>
                        Role Permissions
                      </Typography>
                      {roles
                        .slice()
                        .sort((a, b) => {
                          const order = { owner: 0, admin: 1, billing_manager: 2 };
                          return (order[a.role_key] ?? 999) - (order[b.role_key] ?? 999);
                        })
                        .map((role) => (
                          <Typography key={role.role_key} variant="body2" mb={0.5}>
                            <strong>{role.role_name}:</strong>{' '}
                            {ROLE_DESCRIPTIONS[role.role_key] || role.description}
                          </Typography>
                        ))}
                    </Box>
                  }
                  arrow
                  placement="right"
                >
                  <IconButton size="small" sx={{ p: 0.5 }}>
                    <InfoOutlinedIcon sx={{ fontSize: '17px', color: '#555', ml: 0.5, mb: 0.25 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <FormControl fullWidth>
                <Select
                  value={inviteRoleKey}
                  onChange={(e) => setInviteRoleKey(e.target.value)}
                  sx={{
                    borderRadius: '14px',
                    fontSize: '0.875rem',
                  }}
                >
                  {roles
                    .slice()
                    .sort((a, b) => {
                      const order = { owner: 0, admin: 1, billing_manager: 2 };
                      return (order[a.role_key] ?? 999) - (order[b.role_key] ?? 999);
                    })
                    .map((role) => (
                      <MenuItem key={role.role_key} value={role.role_key}>
                        {role.role_name}
                      </MenuItem>
                    ))}
                </Select>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block', lineHeight: 1.4 }}
                >
                  {ROLE_DESCRIPTIONS[inviteRoleKey] ||
                    roles.find((r) => r.role_key === inviteRoleKey)?.description}
                </Typography>
              </FormControl>
            </Box>

            <PhoneNumberInput
              label="Phone Number (optional)"
              value={phoneNumber}
              onChange={(fullNumber) => {
                setPhoneNumber(fullNumber);
                if (phoneError) setPhoneError(false);
              }}
              error={phoneError}
              helperText={
                phoneError
                  ? phoneNumber.trim()
                    ? 'Please enter a valid phone number'
                    : 'Phone number is required'
                  : undefined
              }
            />

            <Box>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography fontSize="16px" fontWeight={400}>
                  Description (optional)
                </Typography>
                <Tooltip
                  title="Add a short description to help identify this team member (e.g. Accountant)."
                  placement="top-end"
                >
                  <IconButton>
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: '17px',
                        color: '#555',
                        ml: 0.5,
                        mb: 0.25,
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                value={description}
                onChange={(e) => {
                  if (e.target.value.length <= 32) {
                    setDescription(e.target.value);
                  }
                }}
                placeholder="e.g. Accountant"
                variant="outlined"
                fullWidth
                helperText={`${description.length}/32 characters`}
              />
            </Box>

            <Box>
              <Typography fontSize="16px" fontWeight={400} mb={1}>
                Invite Email Text (optional)
              </Typography>
              <TextField
                placeholder="Hi Jane, please join our team!"
                value={inviteMemo}
                onChange={(e) => setInviteMemo(e.target.value)}
                variant="outlined"
                fullWidth
                multiline
                minRows={2}
              />
            </Box>
          </Stack>
        </DialogContent>

        <MuiDivider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey' }} />

        <DialogActions sx={{ justifyContent: 'space-between !important', margin: 2 }}>
          <SWLoadingButton variant="outlined" onClick={closeDialog} disabled={inviteLoading}>
            Cancel
          </SWLoadingButton>
          <SWLoadingButton
            variant="contained"
            color="primary"
            onClick={handleInvite}
            loading={inviteLoading}
          >
            Send Invite
          </SWLoadingButton>
        </DialogActions>
      </Dialog>

      {/* Vertical Menu for Active Users */}
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditClick}>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Edit User Dialog */}
      <Dialog
        open={editUserDialogOpen}
        onClose={handleEditCancel}
        aria-labelledby="edit-user-dialog-title"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <GlobalStyles
          styles={{
            '.MuiOutlinedInput-root': {
              borderRadius: '14px !important',
              minHeight: '45px !important',
              padding: '0 !important',
            },
            '.MuiOutlinedInput-input': {
              padding: '8px 12px !important',
              lineHeight: '1.5em',
            },
            '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              boxShadow: '0 0 0 2px rgba(71,144,204,0.2) !important',
            },
          }}
        />
        <DialogTitle
          id="edit-user-dialog-title"
          sx={{ margin: 'auto', textAlign: 'center', paddingTop: 3 }}
        >
          <Stack direction={'column'}>
            <Box sx={{ fontSize: '32px', fontWeight: 'bold' }}>Edit Team Member</Box>
            <Box sx={{ fontSize: '16px', color: 'text.secondary' }}>
              Update this user's phone number and description
            </Box>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={handleEditCancel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <MuiDivider
          variant="fullWidth"
          sx={{ borderWidth: 1, borderColor: 'grey', marginTop: 2 }}
        />

        <DialogContent sx={{ margin: 3 }}>
          <Stack spacing={3}>
            <Box>
              <Typography fontSize="16px" fontWeight={400} mb={1}>
                Email Address *
              </Typography>
              <TextField
                value={editingUser?.email || ''}
                variant="outlined"
                fullWidth
                disabled
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              />
            </Box>

            <Box>
              <Typography fontSize="16px" fontWeight={400} mb={1}>
                Role
              </Typography>
              <TextField
                value={
                  roles.find((r) => r.role_key === editingUser?.role_key)?.role_name ||
                  editingUser?.role_key ||
                  'Owner'
                }
                variant="outlined"
                fullWidth
                disabled
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
                helperText="To change the role, use the dropdown in the team table"
              />
            </Box>

            <PhoneNumberInput
              label="Phone Number (optional)"
              value={editUserPhone}
              onChange={(fullNumber) => setEditUserPhone(fullNumber)}
            />

            <Box>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography fontSize="16px" fontWeight={400}>
                  Description (optional)
                </Typography>
                <Tooltip
                  title="Add a short description to help identify this team member (e.g. Accountant)."
                  placement="top-end"
                >
                  <IconButton>
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: '17px',
                        color: '#555',
                        ml: 0.5,
                        mb: 0.25,
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                value={editUserNote}
                onChange={(e) => {
                  if (e.target.value.length <= 32) {
                    setEditUserNote(e.target.value);
                  }
                }}
                placeholder="Enter description (optional)"
                variant="outlined"
                fullWidth
                helperText={`${editUserNote.length}/32 characters`}
              />
            </Box>
          </Stack>
        </DialogContent>

        <MuiDivider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey' }} />

        <DialogActions sx={{ justifyContent: 'space-between !important', margin: 2 }}>
          <SWLoadingButton variant="outlined" onClick={handleEditCancel} disabled={updatingUser}>
            Cancel
          </SWLoadingButton>
          <SWLoadingButton
            variant="contained"
            color="primary"
            onClick={handleEditSave}
            loading={updatingUser}
          >
            Save Changes
          </SWLoadingButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-user-dialog-title"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          id="delete-user-dialog-title"
          sx={{ margin: 'auto', textAlign: 'center', paddingTop: 3 }}
        >
          <Stack direction={'column'}>
            <Box sx={{ fontSize: '32px', fontWeight: 'bold' }}>Delete Team Member</Box>
            <Box sx={{ fontSize: '16px', color: 'text.secondary' }}>
              This action cannot be undone
            </Box>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={handleDeleteCancel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <MuiDivider
          variant="fullWidth"
          sx={{ borderWidth: 1, borderColor: 'grey', marginTop: 2 }}
        />

        <DialogContent sx={{ margin: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to remove <strong>{userToDelete?.email}</strong> from your
            account?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This user will lose access to all structures, units, and billing information for this
            account. If this user is not part of any other accounts, their account will be
            permanently deleted.
          </Typography>
        </DialogContent>

        <MuiDivider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey' }} />

        <DialogActions sx={{ justifyContent: 'space-between !important', margin: 2 }}>
          <SWLoadingButton variant="outlined" onClick={handleDeleteCancel} disabled={deletingUser}>
            Cancel
          </SWLoadingButton>
          <SWLoadingButton
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            loading={deletingUser}
          >
            Delete Team Member
          </SWLoadingButton>
        </DialogActions>
      </Dialog>

      {/* Self-Downgrade Confirmation Dialog */}
      <Dialog
        open={downgradeDialogOpen}
        onClose={handleDowngradeCancel}
        aria-labelledby="downgrade-role-dialog-title"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          id="downgrade-role-dialog-title"
          sx={{ margin: 'auto', textAlign: 'center', paddingTop: 3 }}
        >
          <Stack direction={'column'}>
            <Box sx={{ fontSize: '32px', fontWeight: 'bold' }}>Downgrade Your Role?</Box>
            <Box sx={{ fontSize: '16px', color: 'text.secondary' }}>
              This will reduce your permissions
            </Box>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={handleDowngradeCancel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <MuiDivider
          variant="fullWidth"
          sx={{ borderWidth: 1, borderColor: 'grey', marginTop: 2 }}
        />

        <DialogContent sx={{ margin: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are about to change your own role from <strong>Owner</strong> to{' '}
            <strong>
              {roles.find((r) => r.role_key === pendingDowngrade?.newRoleKey)?.role_name ||
                pendingDowngrade?.newRoleKey}
            </strong>
            .
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You will lose the ability to manage owner roles and may not be able to reverse this
            change without another owner&apos;s help. Only proceed if you are sure.
          </Typography>
        </DialogContent>

        <MuiDivider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey' }} />

        <DialogActions sx={{ justifyContent: 'space-between !important', margin: 2 }}>
          <SWLoadingButton variant="outlined" onClick={handleDowngradeCancel}>
            Cancel
          </SWLoadingButton>
          <SWLoadingButton variant="contained" color="warning" onClick={handleDowngradeConfirm}>
            Yes, Downgrade My Role
          </SWLoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
